import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import Seo, { localBusinessLd, breadcrumbLd } from "@/lib/seo";
import api from "@/lib/api";

export default function ContactPage() {
  const [company, setCompany] = useState({});
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", message: "", creneau: "" });

  useEffect(() => { api.get("/company").then(({ data }) => setCompany(data)).catch(() => {}); }, []);

  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.telephone || !form.message) { toast.error("Merci de renseigner nom, téléphone et message."); return; }
    setSending(true);
    try {
      await api.post("/callback", {
        nom: form.nom, telephone: form.telephone, email: form.email,
        sujet: form.message, creneau_souhaite: form.creneau || "",
      });
      toast.success("Message reçu. Nous vous rappelons rapidement.");
      setForm({ nom: "", email: "", telephone: "", message: "", creneau: "" });
    } catch { toast.error("Envoi impossible pour le moment, appelez-nous au 06 27 44 32 30."); }
    finally { setSending(false); }
  };

  return (
    <div className="pt-[100px] md:pt-[140px] pb-24">
      <Seo
        title="Contact — Entreprise de rénovation à Villepinte, Paris & Île-de-France | RAMA DÉCO"
        description="Contactez RAMA DÉCO : 30B rue des Mésanges, 93420 Villepinte. Téléphone 06 27 44 32 30, contact@ramadeco.fr, du lundi au samedi de 8h à 19h. Devis gratuit."
        path="/contact"
        jsonLd={[localBusinessLd(), breadcrumbLd([{ to: "/", label: "Accueil" }, { to: "/contact", label: "Contact" }])]}
      />
      <div className="wrap grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="font-mono-caption text-bordeaux mb-4">Contact</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tight text-stone-900 leading-[1.05]">Parlons de <span className="italic text-bordeaux">votre projet.</span></h1>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed">Une question, une visite, un devis : plusieurs façons de nous joindre.</p>

          <div className="mt-10 space-y-4">
            <a href="tel:0627443230" data-testid="contact-phone" className="flex items-center gap-4 p-4 bg-white border border-stone-300 rounded-sm hover:border-bordeaux transition">
              <Phone className="text-bordeaux" size={20} />
              <div>
                <div className="text-xs text-stone-500 uppercase tracking-widest">Téléphone</div>
                <div className="font-display text-lg text-stone-900">{company.telephone || "06 27 44 32 30"}</div>
              </div>
            </a>
            <a href={`mailto:${company.email || "sasu.ramadeco@gmail.com"}`} data-testid="contact-email" className="flex items-center gap-4 p-4 bg-white border border-stone-300 rounded-sm hover:border-bordeaux transition">
              <Mail className="text-bordeaux" size={20} />
              <div>
                <div className="text-xs text-stone-500 uppercase tracking-widest">Email</div>
                <div className="font-display text-lg text-stone-900">{company.email || "sasu.ramadeco@gmail.com"}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 bg-white border border-stone-300 rounded-sm">
              <MapPin className="text-bordeaux" size={20} />
              <div>
                <div className="text-xs text-stone-500 uppercase tracking-widest">Adresse</div>
                <div className="font-display text-lg text-stone-900">{company.adresse || "30 Bis Rue des Mésanges, 93420 Villepinte"}</div>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-white border border-stone-300 rounded-sm">
              <Clock className="text-bordeaux" size={20} />
              <div>
                <div className="text-xs text-stone-500 uppercase tracking-widest">Horaires</div>
                <div className="font-display text-lg text-stone-900">{company.horaires || "Lundi — Samedi, 8h — 19h"}</div>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-white border border-stone-300 p-8 md:p-12 rounded-sm">
          <div className="font-mono-caption text-stone-500 mb-4">Nous écrire</div>
          <h2 className="font-display text-3xl text-stone-900 mb-8">Envoyez-nous un message</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-mono-caption text-stone-500 block mb-2">Nom complet *</label>
              <input data-testid="contact-form-name" value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} className="w-full border-b border-stone-400 bg-transparent py-3 text-stone-900 focus:border-bordeaux outline-none" />
            </div>
            <div>
              <label className="text-xs font-mono-caption text-stone-500 block mb-2">Téléphone *</label>
              <input data-testid="contact-form-phone" value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} className="w-full border-b border-stone-400 bg-transparent py-3 text-stone-900 focus:border-bordeaux outline-none" />
            </div>
          </div>
          <div className="mt-6">
            <label className="text-xs font-mono-caption text-stone-500 block mb-2">Quand vous rappeler ?</label>
            <input data-testid="contact-form-creneau" placeholder="ex. demain matin, en fin de journée…" value={form.creneau} onChange={e => setForm({...form, creneau: e.target.value})} className="w-full border-b border-stone-400 bg-transparent py-3 text-stone-900 focus:border-bordeaux outline-none" />
          </div>
          <div className="mt-6">
            <label className="text-xs font-mono-caption text-stone-500 block mb-2">Email</label>
            <input data-testid="contact-form-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border-b border-stone-400 bg-transparent py-3 text-stone-900 focus:border-bordeaux outline-none" />
          </div>
          <div className="mt-6">
            <label className="text-xs font-mono-caption text-stone-500 block mb-2">Votre message</label>
            <textarea data-testid="contact-form-message" rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full border-b border-stone-400 bg-transparent py-3 text-stone-900 focus:border-bordeaux outline-none resize-none" />
          </div>
          <button type="submit" disabled={sending} data-testid="contact-form-submit" className="btn-primary mt-8 disabled:opacity-60">{sending ? "Envoi…" : "Demander un rappel"}</button>
          <p className="mt-4 text-xs text-stone-500">Nom, téléphone et message sont nécessaires pour vous rappeler. Vos coordonnées ne servent qu'à traiter votre demande.</p>
        </form>
      </div>
    </div>
  );
}
